import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Slider, Stack, Typography, Paper, Card, CardContent, LinearProgress, Divider, Chip, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { CustomButton } from '../../../../../custom/components';
import styles from './airlines.module.css';

const LOGO_WIDTH = 300;
const LOGO_HEIGHT = 100;

const AirlineLogoUpload = ({ preview, onChange, onPendingChange }) => {
  const [image, setImage] = useState(null);
  const [position, setPosition] = useState({
    x: 50,
    y: 50,
    zoom: 1
  });
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [failedPreview, setFailedPreview] = useState(null);
  const [processing, setProcessing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectionRef = useRef(0);

  useEffect(() => () => { selectionRef.current += 1; }, []);

  useEffect(() => {
    if (!image) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      setError('Your browser cannot crop images. Please upload a 200x300 image.');
      return;
    }
    const scale = Math.min(image.naturalWidth / LOGO_WIDTH, image.naturalHeight / LOGO_HEIGHT) / position.zoom;
    const width = LOGO_WIDTH * scale;
    const height = LOGO_HEIGHT * scale;
    const x = (image.naturalWidth - width) * position.x / 100;
    const y = (image.naturalHeight - height) * position.y / 100;
    context.clearRect(0, 0, LOGO_WIDTH, LOGO_HEIGHT);
    context.drawImage(image, x, y, width, height, 0, 0, LOGO_WIDTH, LOGO_HEIGHT);
  }, [image, position]);

  const selectFile = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const selection = ++selectionRef.current;
    setImage(null);
    setError('');
    setProcessing(false);
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Choose a PNG, JPEG or WebP image.');
      onPendingChange(false);
      return;
    }
    onPendingChange(true);
    setProcessing(true);
    const fail = () => {
      if (selection !== selectionRef.current) return;
      setError('Unable to read this image. Please choose another file.');
      setProcessing(false);
      onPendingChange(false);
    };
    const reader = new FileReader();
    reader.onerror = fail;
    reader.onabort = fail;
    reader.onload = () => {
      if (selection !== selectionRef.current) return;
      const selectedImage = new Image();
      selectedImage.onerror = fail;
      selectedImage.onload = () => {
        if (selection !== selectionRef.current) return;
        setProcessing(false);
        if (selectedImage.naturalWidth === LOGO_WIDTH && selectedImage.naturalHeight === LOGO_HEIGHT) {
          onChange(file, reader.result);
          onPendingChange(false);
        } else {
          setPosition({
            x: 50,
            y: 50,
            zoom: 1
          });
          setImage(selectedImage);
        }
      };
      selectedImage.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const cancelCrop = () => {
    selectionRef.current += 1;
    setImage(null);
    setError('');
    setProcessing(false);
    setDragActive(false);
    setFailedPreview(null);
    onChange(null, null);
    onPendingChange(false);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const syntheticEvent = {
        target: {
          files,
          value: '' 
        } 
      };
      selectFile(syntheticEvent);
    }
  };

  const resetPosition = () => {
    setPosition({
      x: 50,
      y: 50,
      zoom: 1
    });
  };

  const applyCrop = () => {
    const selection = selectionRef.current;
    const canvas = canvasRef.current;
    setProcessing(true);
    setError('');
    const fail = () => {
      setProcessing(false);
      setError('Unable to crop this image. Please try again or choose another file.');
    };
    try {
      if (!canvas.getContext('2d')) {
        fail();
        return;
      }
      const croppedPreview = canvas.toDataURL('image/png');
      canvas.toBlob((blob) => {
        if (selection !== selectionRef.current) return;
        if (!blob) {
          fail();
          return;
        }
        onChange(new File([blob], 'airline-logo.png', { type: 'image/png' }), croppedPreview);
        setImage(null);
        setProcessing(false);
        onPendingChange(false);
      }, 'image/png');
    } catch (cropError) {
      console.error('Unable to crop airline logo:', cropError);
      fail();
    }
  };

  return (
    <Box className={styles.container}>
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Typography variant='body2' color='textSecondary' className={styles.uploadDescription}>
          Upload an airline logo (optional).
        </Typography>
        {/* Upload Section */}
        {!image && !preview && (
          <>
            <Card
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`${styles.uploadCard} ${dragActive ? styles.dragActive : ''}`}
              elevation={dragActive ? 2 : 0}
            >
              <CardContent>
                <Stack spacing={1} alignItems='center' justifyContent='center'>
                  <Box className={`${styles.iconCircle} ${dragActive ? styles.pulse : ''}`}>
                    <CloudUploadIcon className={styles.cloudIcon} color='primary' />
                  </Box>
                  <Typography variant='body2' color='textSecondary' align='center' className={styles.uploadDescription}>
                    Drag and drop your logo here or click to select
                  </Typography>
                  <Stack direction='row' spacing={1} className={styles.fileChips}>
                    <Chip label='PNG' size='small' variant='outlined' />
                    <Chip label='JPEG' size='small' variant='outlined' />
                  </Stack>
                  <Typography variant='caption' color='textSecondary' className={styles.fileFormatHint}>
                    Recommended: {LOGO_WIDTH}×{LOGO_HEIGHT}px
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
            <input
              ref={fileInputRef}
              type='file'
              accept='image/png,image/jpeg,image/webp'
              onChange={selectFile}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity='error' className={styles.errorAlert}>
            {error}
          </Alert>
        )}

        {/* Processing Indicator */}
        {processing && (
          <Card className={styles.processingCard}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1 
                }}>
                  <Box className={`${styles.processingDot} ${styles.blinking}`} />
                  <Typography variant='body2' className={styles.processingText}>
                    Processing your logo...
                  </Typography>
                </Box>
                <LinearProgress className={styles.progressBar} />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Crop Editor */}
        {image && (
          <Card elevation={0} className={styles.cropCard}>
            <CardContent>
              <Stack spacing={2}>
                {/* Canvas Preview */}
                <Paper elevation={0} className={styles.canvasPreview}>
                  <canvas
                    ref={canvasRef}
                    width={LOGO_WIDTH}
                    height={LOGO_HEIGHT}
                    aria-label='Logo crop preview'
                    className={styles.canvas}
                  />
                </Paper>

                {/* Sliders */}
                <Box compoanent='div'>
                  <Stack direction='column' spacing={1} className={styles.sliderStack}>
                    {[
                      {
                        key: 'x',
                        label: 'Horizontal Position',
                        min: 0,
                        max: 100,
                        step: 1
                      },
                      {
                        key: 'y',
                        label: 'Vertical Position',
                        min: 0,
                        max: 100,
                        step: 1
                      },
                      {
                        key: 'zoom',
                        label: 'Zoom Level',
                        min: 0.1,
                        max: 3,
                        step: 0.01
                      }
                    ].map(({ key, label, min, max, step }) => (
                      <Stack direction='row' spacing={3} key={key}>
                        <Typography variant='body2' className={styles.sliderLabel}>
                          {label}
                        </Typography>
                        <Slider
                          aria-label={label}
                          value={position[key]}
                          min={min}
                          max={max}
                          step={step}
                          disabled={processing}
                          onChange={(_event, value) =>
                            setPosition((previous) => ({
                              ...previous,
                              [key]: value
                            }))
                          }
                          marks={
                            key === 'zoom'
                              ? [
                                {
                                  value: 0.1,
                                  label: '0.1x' 
                                },
                                {
                                  value: 1,
                                  label: '1x' 
                                },
                                {
                                  value: 3,
                                  label: '3x' 
                                }
                              ]
                              : false
                          }
                          sx={{
                            '& .MuiSlider-thumb': {
                              transition: 'all 0.2s',
                              '&:hover': { boxShadow: '0 0 0 8px rgba(33, 150, 243, 0.16)' }
                            }
                          }}
                          size='medium'
                        />
                        <Box className={styles.sliderValue}>
                          {position[key].toFixed(key === 'zoom' ? 2 : 0)}{key !== 'zoom' && '%'}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>

                <Divider className={styles.divider} />

                {/* Action Buttons */}
                <Stack direction='row' spacing={1.5} sx={{ justifyContent: 'space-between' }} className={styles.buttonGroup}>
                  <Tooltip title='Reset to default position and zoom'>
                    <Box>
                      <CustomButton
                        startIcon={<RestartAltIcon />}
                        variant='text'
                        size='small'
                        onClick={resetPosition}
                        disabled={processing}
                        className={styles.actionButton}
                        fullWidth
                      >
                      Reset
                      </CustomButton>
                    </Box>
                  </Tooltip>
                  <Stack direction='row' spacing={1.5}>
                    <CustomButton
                      type='button'
                      variant='contained'
                      disabled={processing}
                      onClick={applyCrop}
                      fullWidth
                      className={styles.actionButton}
                    >
                    Apply Crop
                    </CustomButton>
                    <CustomButton
                      type='button'
                      variant='outlined'
                      onClick={cancelCrop}
                      disabled={processing}
                      fullWidth
                      className={styles.actionButton}
                    >
                    Cancel
                    </CustomButton>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Preview Section */}
        {preview && (
          <Card elevation={0} className={styles.previewCard}>
            <CardContent>
              <Stack spacing={2.5}>
                <Box className={styles.successHeader}>
                  <CheckCircleIcon className={styles.successIcon} />
                  <Box>
                    <Typography variant='body2' className={styles.successTitle}>
                      Logo Uploaded Successfully! Your airline logo is ready to use.
                    </Typography>
                  </Box>
                </Box>

                {/* Preview Image */}
                <Paper elevation={0} className={styles.previewImageContainer}>
                  <img
                    key={preview}
                    src={preview}
                    alt='Airline logo preview'
                    className={styles.previewImage}
                    crossOrigin={
                      preview.startsWith('data:') || preview.startsWith('blob:')
                        ? undefined
                        : 'use-credentials'
                    }
                    onError={() => setFailedPreview(preview)}
                    onLoad={() => setFailedPreview(null)}
                  />
                </Paper>

                {failedPreview === preview && (
                  <Alert severity='error'>
                    Unable to load the saved logo. Please check that the file is available or upload a replacement.
                  </Alert>
                )}

                <CustomButton
                  className={styles.cancelButton}
                  onClick={cancelCrop}
                  fullWidth
                >
                    REUPLOAD LOGO
                </CustomButton>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
};

export default AirlineLogoUpload;
